import { Component, DOCUMENT, Inject, OnDestroy, OnInit, Renderer2 } from '@angular/core';
import { MAT_TOOLTIP_DEFAULT_OPTIONS } from '@angular/material/tooltip';
import { combineLatest, Subscription } from 'rxjs';
import { DialLogNavState, DialLogsDrawerData, DialLogsTimelineDrawerService } from './dial-logs-timeline-drawer.service';
import { DIAL_LOGS_TOOLTIP_OPTIONS } from './dial-logs-timeline.tooltip';
import { displayEntityName } from './dial-logs-timeline.model';

/**
 * In-layout dial-logs drawer host. Rendered as a flex sibling of the app's
 * <main> so it pushes/shares space with the page content (rather than overlaying
 * it) and sits under the top header. Resizable via a left-edge drag handle.
 */
@Component({
    selector: 'app-dial-logs-timeline-drawer',
    templateUrl: './dial-logs-timeline-drawer.component.html',
    styleUrls: ['./dial-logs-timeline-drawer.component.scss'],
    providers: [{ provide: MAT_TOOLTIP_DEFAULT_OPTIONS, useValue: DIAL_LOGS_TOOLTIP_OPTIONS }],
    standalone: false,
})
export class DialLogsTimelineDrawerComponent implements OnInit, OnDestroy {
    open = false;
    data: DialLogsDrawerData | null = null;
    width = DialLogsTimelineDrawerService.MIN_WIDTH;
    resizing = false;
    nav: DialLogNavState = { index: -1, total: 0, hasPrev: false, hasNext: false };
    /** Whether the (CSS-truncated) name is actually overflowing — set on hover; gates its
     * tooltip so we only show the full name when it's genuinely cut off. */
    nameTruncated = false;

    private subscription = new Subscription();
    // <style> element that highlights the active entity's row in whatever list
    // page is behind the drawer (identities, services, …). One place, all lists.
    private highlightStyle?: HTMLStyleElement;
    private startX = 0;
    private startWidth = 0;
    private readonly moveHandler = (e: MouseEvent) => this.onResizeMove(e);
    private readonly upHandler = () => this.endResize();

    constructor(
        public drawer: DialLogsTimelineDrawerService,
        private renderer: Renderer2,
        @Inject(DOCUMENT) private document: Document
    ) {}

    ngOnInit(): void {
        this.subscription.add(this.drawer.open$.subscribe((o) => (this.open = o)));
        this.subscription.add(this.drawer.data$.subscribe((d) => (this.data = d)));
        this.subscription.add(this.drawer.width$.subscribe((w) => (this.width = w)));
        this.subscription.add(this.drawer.nav$.subscribe((n) => (this.nav = n)));
        // Highlight the open entity's row in the list behind the drawer.
        this.subscription.add(
            combineLatest([this.drawer.open$, this.drawer.data$]).subscribe(([open, data]) =>
                this.highlightActiveRow(open ? data?.model?.id : null)
            )
        );
    }

    ngOnDestroy(): void {
        this.detachResizeListeners();
        this.subscription.unsubscribe();
        this.highlightStyle?.remove();
    }

    /**
     * Tint the ag-grid row for the entity whose drawer is open. Matching by the
     * stable `row-id` attribute (set from the entity id) via an injected CSS rule
     * survives ag-grid's row virtualization — a class on a recycled node wouldn't.
     * Works for any ziti list page (they all render `#ItemGrid`).
     */
    private highlightActiveRow(id: string | null | undefined): void {
        if (!this.highlightStyle) {
            this.highlightStyle = this.renderer.createElement('style');
            this.renderer.appendChild(this.document.head, this.highlightStyle);
        }
        const styleEl = this.highlightStyle;
        if (!styleEl) return;
        // Guard the selector — ziti ids are base62, so anything else is dropped.
        const safe = id && /^[A-Za-z0-9._-]+$/.test(id) ? id : null;
        // Tint the row's cells (they paint over the row bg) across all three
        // ag-grid segments, plus one accent bar on the leftmost (pinned-left) one.
        styleEl.textContent = safe
            ? `#ItemGrid .ag-row[row-id="${safe}"] .ag-cell{background-color:color-mix(in srgb,var(--primaryColor) 13%,transparent)!important;}` +
              `#ItemGrid .ag-pinned-left-cols-container .ag-row[row-id="${safe}"]{box-shadow:inset 3px 0 0 var(--primaryColor);}`
            : '';
    }

    /** Entity name rendered faithfully (no segments dropped); truncated in the UI. */
    get displayName(): string {
        return displayEntityName(this.data?.model?.name) ?? this.data?.model?.name ?? 'Dial Logs';
    }

    /** Eyebrow describing the entity type. */
    get eyebrow(): string {
        return this.data?.resourceType === 'service' ? 'Service Dial Logs' : 'Identity Dial Logs';
    }

    /** Noun for the pager tooltips. */
    get entityNoun(): string {
        return this.data?.resourceType === 'service' ? 'service' : 'identity';
    }

    /** Router link to this resource's details page (same routes the drawer body's entity links
     * use); null when there's no id to link to. Service → advanced overview; identity → detail. */
    get detailLink(): any[] | null {
        const id = this.data?.model?.id;
        if (!id) {
            return null;
        }
        return this.data?.resourceType === 'service' ? ['/services/advanced', id, 'overview'] : ['/identities', id];
    }

    /** Page to the previous (-1) / next (1) entity in the filtered list. */
    navigate(direction: -1 | 1): void {
        this.drawer.navigate(direction);
    }

    /** The tab @Input rejects null; hand it undefined when there's no data yet. */
    get tabData(): DialLogsDrawerData | undefined {
        return this.data ?? undefined;
    }

    close(): void {
        this.drawer.close();
    }

    startResize(event: MouseEvent): void {
        event.preventDefault();
        this.resizing = true;
        this.startX = event.clientX;
        this.startWidth = this.width;
        this.document.addEventListener('mousemove', this.moveHandler);
        this.document.addEventListener('mouseup', this.upHandler);
        // Suppress text selection while dragging.
        this.renderer.addClass(this.document.body, 'dlt-drawer-resizing');
    }

    private onResizeMove(event: MouseEvent): void {
        if (!this.resizing) return;
        // Dragging the handle left (smaller clientX) widens the drawer.
        const next = this.startWidth + (this.startX - event.clientX);
        this.width = Math.min(DialLogsTimelineDrawerService.MAX_WIDTH, Math.max(DialLogsTimelineDrawerService.MIN_WIDTH, Math.round(next)));
    }

    private endResize(): void {
        if (!this.resizing) return;
        this.resizing = false;
        this.detachResizeListeners();
        this.renderer.removeClass(this.document.body, 'dlt-drawer-resizing');
        this.drawer.setWidth(this.width);
    }

    private detachResizeListeners(): void {
        this.document.removeEventListener('mousemove', this.moveHandler);
        this.document.removeEventListener('mouseup', this.upHandler);
    }
}
